import { isOnline } from '@/const/env';

/**
 * d
 * - basic_functions: 基础功能埋点
 * - ai_messages: AI 消息埋点
 * - commercial: 商业化埋点
 */
export type CollectTheme = 'basic_functions' | 'ai_messages' | 'commercial';

/**
 * 事件名称到数据表的映射
 */
export const ACTION_TO_THEME_MAP = {
  // 默认都是 basic_functions
  open_plan_modal: 'commercial',
  open_plan_landing_page: 'commercial',
  checkout_open: 'commercial',
  stripe_checkout_retry: 'commercial',
  stripe_checkout_retry_show: 'commercial',
  pay_success: 'commercial',
  pay_fail: 'commercial',
  update_method_cta_show: 'commercial',
  update_method_cta_click: 'commercial',
  remix_plan_model: 'commercial',
  remix_plan_model_tab: 'commercial',
  ai2ui_form_show: 'basic_functions',
  ai2ui_click: 'basic_functions',
  prepay_cta_show: 'commercial',
  prepay_cta_click: 'commercial',
  prepay_payment_modal_open: 'commercial',
  prepay_payment_open_duration: 'basic_functions',
  prepay_payment_success: 'commercial',
  prepay_payment_retention_show: 'commercial',
  credit_purchase_modal_open: 'commercial',
  credits_modal_open: 'basic_functions',
  settings_modal_open: 'basic_functions',
  settings_tab_view: 'basic_functions',
  primary_domain_click: 'basic_functions',
  email_change_submit: 'basic_functions',
  manage_billing_click: 'basic_functions',
  subscription_cancel_retention: 'basic_functions',
  new_project_prompt_prefill: 'basic_functions',
  login_click: 'basic_functions',
  user_signup: 'basic_functions',
  user_login: 'basic_functions',
  chat_timeout: 'basic_functions',
  chat_error: 'basic_functions',
  chat_error_retry: 'basic_functions',
  page_oops: 'basic_functions',
  page_oops_reload_click: 'basic_functions',
  page_view_wegicai: 'basic_functions',
  website_generate_status: 'ai_messages',
  website_check_after_chat: 'ai_messages',
  website_has_error: 'ai_messages',
  call_timmy_show: 'basic_functions',
  call_timmy_click: 'basic_functions',
  generate_feedback_submit: 'basic_functions',
  generate_feedback_show: 'basic_functions',
  generate_feedback_click: 'basic_functions',
  feedback_modal_open: 'basic_functions',
  feedback_modal_close: 'basic_functions',
  feedback_modal_submit: 'basic_functions',
  edit_mode_enter: 'basic_functions',
  edit_mode_startup: 'basic_functions',
  edit_mode_message_send: 'basic_functions',
  edit_mode_quota_exceeded: 'basic_functions',
  shortcut_suggestion_show: 'basic_functions',
  shortcut_suggestion_click: 'basic_functions',
  start_generate_show: 'basic_functions',
  start_generate_click: 'basic_functions',
  chat_round_start: 'basic_functions',
  send_message: 'basic_functions',
  open_project: 'basic_functions',
  back_to_chat: 'basic_functions',
  fe_web_performance: 'basic_functions',
  view_mode_change: 'basic_functions',
  panel_mode_switch: 'basic_functions',
  preview_toggle: 'basic_functions',
  sandbox_address_bar_navigate: 'basic_functions',
  sandbox_admin_tab_enter: 'basic_functions',
  chat_queue_item_enqueue: 'basic_functions',
  chat_queue_item_recall: 'basic_functions',
  chat_queue_item_discard: 'basic_functions',
  chat_queue_item_reorder: 'basic_functions',
  // [临时诊断] 队列不变量违背：同一条队列项同时出现在 pending 与 processing。确认修复稳定后请一并移除。
  chat_queue_invariant_pending_processing_overlap: 'basic_functions',
  website_delete: 'basic_functions',
  file_upload_click: 'basic_functions',
  file_upload_error: 'basic_functions',
  open_help_docs: 'basic_functions',
  open_publish_panel: 'basic_functions',
  custom_domain_entry: 'basic_functions',
  custom_domain_modify: 'basic_functions',
  custom_domain_connect: 'basic_functions',
  custom_domain_check: 'basic_functions',

  website_first_generation_completed: 'basic_functions', // 这个埋点后端埋比较合适，由于后端没空，暂时在前端埋，等后端有空迁移到后端去埋
  post_generation_onboarding_start: 'basic_functions',
  initial_generation_journey: 'basic_functions',
  // Voice input (Chat)
  chat_speech_record_start: 'basic_functions',
  chat_speech_record_cancel: 'basic_functions',
  chat_speech_record_stop: 'basic_functions',
  chat_speech_transcribe_start: 'basic_functions',
  chat_speech_transcribe_success: 'basic_functions',
  chat_speech_transcribe_fail: 'basic_functions',
  chat_speech_transcribe_post_edit: 'basic_functions',
  download_code: 'basic_functions',
  call_timmy_publish: 'basic_functions',
  // 版本管理
  version_history_panel_open: 'basic_functions', // 打开历史版本看板
  version_history_favorite_click: 'basic_functions', // 点击收藏/取消收藏历史版本
  version_switch_preview_click: 'basic_functions', // 点击版本预览
  version_switch_restore_click: 'basic_functions', // 点击版本回滚
  version_switch_restore_result: 'basic_functions', // 版本回滚结果
  project_version_created_diagnostic: 'basic_functions', // 版本创建 Socket 及状态处理诊断
  // 网站检测
  site_audit_toggle: 'basic_functions', // 点击网站检测面板最外部入口
  site_audit_start_click: 'basic_functions', // 点击开始检测按钮
  site_audit_retest_click: 'basic_functions', // 点击重新检测按钮
  site_audit_optimize_all_click: 'basic_functions', // 点击全部优化按钮
  site_audit_optimize_item_click: 'basic_functions', // 点击单项优化按钮
  site_audit_tab_switch: 'basic_functions', // 切换 SEO/Performance Tab
  site_audit_category_toggle: 'basic_functions', // 展开/收起分类卡片
  site_audit_cancel_click: 'basic_functions', // 点击取消检测按钮
  site_audit_gsc_guide_click: 'basic_functions', // 点击 GSC 引导按钮
  site_audit_completed: 'basic_functions', // 检测完成
  traffic_analytics_panel_view: 'basic_functions',
  traffic_analytics_click: 'basic_functions',
  cloud_enable_click: 'basic_functions', // Wegic Cloud 启用按钮按钮
  cloud_approval_enable_click: 'basic_functions', // Wegic Cloud 审批卡片确认按钮
  cloud_beta_apply: 'basic_functions', // 审批卡片确认按钮
  reference_ui_show: 'basic_functions', // 风格参考图展示
  reference_ui_image_load_failed: 'basic_functions',
  reference_ui_style_click: 'basic_functions',
  initial_generation_style_analysis_failed: 'basic_functions',
  initial_generation_liquid_glass_fallback: 'basic_functions',
  initial_generation_cursor_dispersion_fallback: 'basic_functions',
  reference_ui_feedback_show: 'basic_functions',
  reference_ui_feedback_click: 'basic_functions',
  reference_ui_feedback_submit: 'basic_functions',
  reference_ui_regeneration_result: 'basic_functions',
  reference_ui_feedback_cancel: 'basic_functions',
  industry_case_exposure: 'basic_functions',
  industry_case_click: 'basic_functions',
  // memory
  todo_item_click: 'basic_functions', // 待办事项点击
  // 站内信
  notification_panel_open: 'basic_functions',
  notification_detail_view: 'basic_functions',
  notification_mark_all_read: 'basic_functions',
  notification_footer_action_click: 'basic_functions',
  create_project: 'basic_functions',
  workspace_sidebar_click: 'basic_functions',
  workspace_template_click: 'basic_functions',
  upgrade_button_click: 'basic_functions',
  trial_onboarding_paywall_shown: 'basic_functions',
  credit_notice_flow: 'basic_functions',
  credit_purchase_entry_click: 'basic_functions',

  guest_login_click: 'basic_functions',
  referral_modal_show: 'commercial',
  referral_invite_link_copy: 'commercial',
  coupon_code_copy: 'commercial',
  coupon_code_show: 'commercial',
  checkout_coupon_show: 'basic_functions',

  // Feedback Trigger System
  feedback_card_show: 'basic_functions',
  feedback_direction_selected: 'basic_functions',
  feedback_issue_selected: 'basic_functions',
  feedback_calibration_plan_generated: 'basic_functions',
  feedback_calibration_plan_failed: 'basic_functions',
  feedback_readiness_checklist_generated: 'basic_functions',
  feedback_readiness_checklist_failed: 'basic_functions',
  feedback_understanding_confirmed: 'basic_functions',
  feedback_readiness_check_started: 'basic_functions',
  feedback_readiness_fix_selected: 'basic_functions',
  feedback_action_selected: 'basic_functions',
  feedback_action_succeeded: 'basic_functions',
  feedback_action_failed: 'basic_functions',
  // 内存监控采样
  memory_sample: 'basic_functions',
  sandbox_content_load: 'basic_functions',
  // 浏览器/主线程/预览生命周期诊断
  runtime_diagnostic: 'basic_functions',
  sandbox_preview_startup: 'basic_functions',
} as const satisfies Record<string, CollectTheme>;

/** 埋点事件名类型 */
export type CollectEventName = keyof typeof ACTION_TO_THEME_MAP;

/** 业务类型标识 */
export const BUSINESS_TYPE = 'wegic';

/** 埋点服务器地址 */
export const COLLECT_SERVER_URL = isOnline ? 'https://data.wegic.ai' : 'https://aws-test-data.geesdev.com';

/** 事件名前缀 */
export const EVENT_PREFIX = 'v2';
